import React, { useContext, useEffect, useState } from "react";
import { format, getMonth } from "date-fns";
import useTSHolidays, {
    useOfficesHolidaysTab,
} from "features/timesheet/holidays/useTSHolidays";

import { COLOR_VALUES } from "shared/config/colors";
import { Calendar } from "shared/components/date-controls/holidays-calendar/Calendar";
import { CalendarDate } from "@internationalized/date";
import { CalendarWrapper } from "./TSHolidays.styles";
import EmptyState from "shared/components/data-display/tables/EmptyState";
import HStack from "shared/components/layout/stack/HStack";
import Loading from "shared/components/feedback/loading/Loading";
import SessionContext from "app/contexts/SessionContext";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import { getCellWidthInPercent } from "features/admin/clients/ClientsTable";
import { getDropdownOptionsNoDefaultVal } from "shared/helper-functions/dropdown/dropdownHelpers";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";

const getColumns = (isSmallScreen) => [
    {
        headCellProps: {
            text: "Date",
            width: getCellWidthInPercent(isSmallScreen ? 328 : 160),
        },
        getBodyCellProps: (cell) => ({
            text: format(new Date(cell.value), "MMM d, yyyy"),
            ellipsisAtLine: isSmallScreen ? 2 : null,
            variant: isSmallScreen ? "Descriptive_1_1" : undefined,
        }),
        accessor: "Date",
    },
    {
        headCellProps: {
            text: "Day",
            width: getCellWidthInPercent(isSmallScreen ? 300 : 160),
        },
        getBodyCellProps: (cell) => ({
            text: cell.row.original.Date
                ? format(new Date(cell.row.original.Date), "EEEE")
                : "-",
            variant: isSmallScreen ? "Descriptive_1_1" : undefined,
        }),
        disableSortBy: true,
        accessor: "Day",
    },
    {
        headCellProps: {
            text: "Length",
            width: getCellWidthInPercent(isSmallScreen ? 250 : 80),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ? "Half Day" : "All Day",
            variant: isSmallScreen ? "Descriptive_1_1" : undefined,
        }),
        disableSortBy: true,
        accessor: "HalfDay",
    },
    {
        headCellProps: {
            text: "Name",
            width: getCellWidthInPercent(isSmallScreen ? 328 : 752),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
            variant: isSmallScreen ? "Descriptive_1_1" : undefined,
        }),
        disableSortBy: true,
        accessor: "Name",
    },
];

const viewHolidaysAs = {
    list: "list",
    calendar: "calendar",
};

const DropdownNames = {
    viewAs: "ViewAs",
    year: "Year",
    location: "Location",
};

const getYearsDropdownOptions = () => {
    const currentYear = new Date().getFullYear();
    const yearsList = Array.from({ length: 6 }, (_d, i) => currentYear - i);
    let yearDropdownOptions = [];
    yearsList.forEach((d) => yearDropdownOptions.push({ value: d, label: d }));

    return yearDropdownOptions;
};

const FiltersConfig = [
    {
        filterLabel: "View",
        name: DropdownNames.viewAs,
        columnAccessor: DropdownNames.viewAs,
        unfilteredValue: viewHolidaysAs.list,
        isSegmentedControl: true,
        options: [
            {
                value: viewHolidaysAs.list,
                label: "List",
            },
            {
                value: viewHolidaysAs.calendar,
                label: "Calendar",
            },
        ],
    },
    {
        name: DropdownNames.year,
        columnAccessor: DropdownNames.year,
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: DropdownNames.year,
        options: getYearsDropdownOptions(),
    },
    {
        name: DropdownNames.location,
        columnAccessor: DropdownNames.location,
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: DropdownNames.location,
    },
];

const getRowProps = (row) => ({
    style: {
        background: row.values.Active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const monthsArrNumeric = Array.from({ length: 12 }, (_d, i) => 1 + i);

export const TSHolidays = () => {
    const isSmallScreen = useSmallBreakpoint();
    const columns = getColumns(isSmallScreen);

    const [filters, setFilters] = useState();
    const [filtersConfigVal, setFiltersConfigVal] = useState(FiltersConfig);
    const filterVal =
        filters?.find((f) => f.id === DropdownNames.viewAs)?.value ??
        viewHolidaysAs.list;
    const sessionData = useContext(SessionContext);

    const { activeOffices } = useOfficesHolidaysTab();
    const { useHolidaysTab } = useTSHolidays();

    const defaultLocation = sessionData.currentOfficeId;
    const defaultYear = new Date().getFullYear();

    const holidays = useHolidaysTab({
        officeId:
            filters?.find((f) => f.id === DropdownNames.location)?.value ??
            defaultLocation,
        year:
            filters?.find((f) => f.id === DropdownNames.year)?.value ??
            defaultYear,
    });

    useEffect(() => {
        if (!activeOffices.isLoading) {

            setFiltersConfigVal((prevFilters) => {

                let newObj = [...prevFilters];

                const activeOfficesIndex = newObj.findIndex((f) => f.name === DropdownNames.location);

                newObj[activeOfficesIndex].options = getDropdownOptionsNoDefaultVal(
                    activeOffices?.data,
                    "OfficeRegionId",
                    "Name"
                );

                return newObj[activeOfficesIndex].options.length > 0 ? prevFilters : newObj;
            });
        }
    }, [activeOffices]);

    if (activeOffices.isLoading) {
        return <Loading />;
    }

    if (activeOffices.isError || holidays.isError) {
        return <>Error</>;
    }

    const calendarYear =
        !holidays.isLoading && holidays?.data[0]?.Date
            ? new Date(holidays?.data[0].Date).getFullYear()
            : defaultYear;

    const renderTabContent = () => {
        return (
            <>
                {filterVal === viewHolidaysAs.list ? (
                    <Table
                        grayBgHeader={true}
                        columns={columns}
                        getRowProps={getRowProps}
                        data={holidays?.data}
                        emptyMessageTableTitle={"Holidays"}
                    />
                ) : (
                    <CalendarWrapper>
                        {holidays?.data?.length > 0 ? (
                            monthsArrNumeric.map((d) => (
                                <Calendar
                                    key={d}
                                    {...{ "aria-label": "holiday" }}
                                    value={new CalendarDate(calendarYear, d, 1)}
                                    holidaysList={holidays?.data?.filter(
                                        (f) =>
                                            getMonth(new Date(f.Date)) + 1 === d
                                    )}
                                />
                            ))
                        ) : (
                            <EmptyState
                                title={"Holidays"}
                                style={{ width: "98%" }}
                            />
                        )}
                    </CalendarWrapper>
                )}
            </>
        );
    };

    return (
        <VStack>
            <HStack align="flex-end" wrap="true">
                    <TableFilters
                        initialFilterData={[
                            {
                                name: DropdownNames.location,
                                value: defaultLocation,
                            },
                            { name: DropdownNames.year, value: defaultYear },
                        ]}
                        filtersConfig={filtersConfigVal}
                        onChange={setFilters}
                    />
                </HStack>
                {holidays.isLoading ? <Loading /> : renderTabContent()}
        </VStack>
    );
};
